import React from 'react';
import PropTypes from 'prop-types';
import { useDispatch } from 'react-redux';

import UTRModal from '../../Modal';
import SavedListModalActions from '../SavedListModalActions';
import useListNameForm from '../useListNameForm';
import { renameSavedPlayerList } from '../../../actions/saved-lists-actions';
import { MAX_LIST_NAME_LENGTH } from '../listErrors';
import countCharacters from '../../../utils/countCharacters';

import styles from './editListNameModal.scss';

// The ticket's data-testid contract pins these exact literals (no `modal.` namespace) — the web
// verifier's Playwright selectors depend on them. Intentional deviation from CLAUDE.md rule 4,
// matching the sibling CreateListModal / AddPlayerToListModal; don't "fix" it to the general convention.
const TESTID_PREFIX = 'edit-list-name-modal';
const NAME_ERROR_ID = `${TESTID_PREFIX}-error`;
const MODAL_TITLE = 'Edit List Name';
const NAME_LABEL = 'List Name';
const CANCEL_TESTID_SUFFIX = 'cancel';
const SAVE_TESTID_SUFFIX = 'save';
const SUCCESS_TOAST_TITLE = 'List Renamed';
const SUCCESS_TOAST_MESSAGE = 'Your list name has been updated.';
const GENERIC_INLINE_ERROR_MESSAGE = 'We couldn\'t rename this list. Please try again.';
const GENERIC_ERROR_TOAST_MESSAGE = 'We couldn\'t rename your list. Please try again.';

const EditListNameModal = ({ onCloseClick, listId, currentName, onRenamed }) => {
    const dispatch = useDispatch();

    const { name, nameError, isSaving, isSaveEnabled, handleNameChange, handleSave, handleKeyDown } = useListNameForm({
        initialName: currentName,
        onCloseClick,
        onSubmit: (newName) => dispatch(renameSavedPlayerList(listId, newName)),
        onSubmitted: onRenamed,
        buildSuccessToast: () => ({ title: SUCCESS_TOAST_TITLE, message: SUCCESS_TOAST_MESSAGE }),
        genericInlineMessage: GENERIC_INLINE_ERROR_MESSAGE,
        genericToastMessage: GENERIC_ERROR_TOAST_MESSAGE,
    });

    return (
        <UTRModal
            title={MODAL_TITLE}
            onCloseClick={onCloseClick}
            className={styles.editListNameModal}
            data-testid={TESTID_PREFIX}
        >
            <div className={`${styles.desktopTitle} d-none d-md-block`} data-testid={`${TESTID_PREFIX}.desktop-title`}>
                {MODAL_TITLE}
            </div>
            <label
                className={styles.fieldLabel}
                htmlFor={`${TESTID_PREFIX}-input`}
                data-testid={`${TESTID_PREFIX}.name-label`}
            >
                {NAME_LABEL}
            </label>
            {/*
              Native input, not the shared Form/Input component: the ticket's data-testid contract
              requires the exact literal id below (the web verifier's Playwright selectors depend on
              it), and Form/Input's controlled mode always suffixes its rendered input/error with
              `.field`/`.error`, which would break an exact-id, real-click-driven contract. Same
              rationale as the sibling CreateListModal.
            */}
            <input
                id={`${TESTID_PREFIX}-input`}
                type="text"
                className={`${styles.nameInput} ${nameError ? styles.nameInputError : ''}`}
                value={name}
                onChange={handleNameChange}
                onKeyDown={handleKeyDown}
                aria-invalid={!!nameError}
                aria-describedby={nameError ? NAME_ERROR_ID : undefined}
                data-testid={`${TESTID_PREFIX}.input`}
            />
            <div className={styles.metaRow}>
                {nameError && (
                    <div
                        id={NAME_ERROR_ID}
                        role="alert"
                        className={styles.nameError}
                        data-testid={`${TESTID_PREFIX}.error`}
                    >
                        {nameError}
                    </div>
                )}
                <div className={styles.charCounter} data-testid={`${TESTID_PREFIX}.char-counter`}>
                    {`${countCharacters(name)}/${MAX_LIST_NAME_LENGTH}`}
                </div>
            </div>
            <SavedListModalActions
                data-testid={TESTID_PREFIX}
                onCancel={onCloseClick}
                onSave={handleSave}
                saveDisabled={!isSaveEnabled || isSaving}
                cancelTestidSuffix={CANCEL_TESTID_SUFFIX}
                primaryTestidSuffix={SAVE_TESTID_SUFFIX}
            />
        </UTRModal>
    );
};

EditListNameModal.propTypes = {
    onCloseClick: PropTypes.func.isRequired,
    listId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
    currentName: PropTypes.string,
    onRenamed: PropTypes.func.isRequired,
};

EditListNameModal.defaultProps = {
    currentName: '',
};

export default EditListNameModal;
